import { Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider, useAuth } from "./auth";
import AppShell from "./components/AppShell";
import Dashboard from "./pages/Dashboard";
import Releases from "./pages/Releases";
import ReleaseDetail from "./pages/ReleaseDetail";
import DjTargets from "./pages/DjTargets";
import Fans from "./pages/Fans";
import FanDetail from "./pages/FanDetail";
import FanSegments from "./pages/FanSegments";
import Settings from "./pages/Settings";
import { Spinner } from "./components/ui/spinner";

// Where users sign in. Auth happens in NOX Private; the smartlink app reads
// the shared session cookie. Configurable per-environment so dev points at
// localhost and prod at NOX Private's URL.
const NOX_PRIVATE_URL = (import.meta.env.VITE_NOX_PRIVATE_URL as string | undefined) ?? "https://noxmusic.fm";

function redirectToNoxPrivateLogin(): null {
  const next = encodeURIComponent(window.location.href);
  window.location.replace(`${NOX_PRIVATE_URL}/login?next=${next}`);
  return null;
}

function Gate({ children }: { children: JSX.Element }) {
  const { loading, user } = useAuth();
  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Spinner />
      </div>
    );
  }
  // No local login page — bounce to NOX Private. The session cookie nox-api
  // sets on `Domain=<apex>` is shared across both apps, so once they sign in
  // there they'll come back here authenticated.
  if (!user) return redirectToNoxPrivateLogin();
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/app" element={<Gate><AppShell /></Gate>}>
          <Route index element={<Dashboard />} />
          <Route path="releases" element={<Releases />} />
          <Route path="releases/:slug" element={<ReleaseDetail />} />
          <Route path="releases/:slug/dj-targets" element={<DjTargets />} />
          <Route path="fans" element={<Fans />} />
          <Route path="fans/segments" element={<FanSegments />} />
          <Route path="fans/:id" element={<FanDetail />} />
          <Route path="settings" element={<Settings />} />
        </Route>
        <Route path="/" element={<Navigate to="/app" replace />} />
        <Route path="*" element={<Navigate to="/app" replace />} />
      </Routes>
    </AuthProvider>
  );
}
